<template>
  <div
    class="item asset"
    @click="onClick"
    @contextmenu.prevent="onContextMenu($event)"
  >
    <div class="thumb" :class="{ folder: getIsRecursiveContainer(id) }">
      <svg-icon
        v-if="getIsRecursiveContainer(id)"
        ref="icon"
        class="folder-icon"
        :width="'52'"
        :height="'52'"
        :fill="'#bcbcbc'"
        icon="app-icons/folder"
        @on-load="onIconLoad"
      />
      <img v-else-if="imageUrl" width="100%" height="100%" :src="imageUrl" />
      <svg-icon v-else :width="'60'" :height="'60'" icon="app-icons/spinner" />
    </div>
    <div ref="nameField" class="name" :title="name">
      <input
        v-if="isEditing"
        ref="nameInput"
        class="nameinput"
        spellcheck="false"
        @blur="onBlurText($event, asset)"
        @keyup.enter="onBlurText($event, asset)"
      />
      <template v-else>
        {{ name }}
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT } from '@/constants.js';
import assetTypes from '../../../vuex/project/types/assets-types.js';
import { isValidWindowsFilename } from '../../../utils.js';
import SvgIcon from '../SvgIcon.vue';

export default {
  components: {
    SvgIcon,
  },

  props: {
    id: {
      type: String,
      default: null,
    },

    index: {
      type: Number,
      default: null,
    },

    containerId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      isSelected: false,
      isEditing: false,
    };
  },

  computed: {
    ...mapGetters('project/data/assets', [
      'getIsRecursiveContainer',
      'getContainedNodes',
      'getDescendantNodes',
      'getCoverId',
      'getCoverIcon',
      'getDefaultIcon',
      'getContainerNode',
      'getIndex',
      'getNode',
      'getRootContainer',
      'getExtension',
      'getSmallImageUrl',
    ]),
    ...mapGetters('project/data/components', {
      getAssetComponentReferences: 'getAssetReferences',
    }),
    ...mapGetters('project/data/elements', {
      getAssetElementReferences: 'getAssetReferences',
    }),
    /**
     * Returns the current asset
     * @returns {Object}
     */
    asset() {
      return this.getNode(this.id);
    },

    /**
     * Return the small image url for the asset
     * @returns {String|null}
     */
    imageUrl() {
      return this.getSmallImageUrl(this.id);
    },

    /**
     * Return the asset file extension
     */
    extension() {
      return this.getExtension(this.id);
    },

    /**
     * Return the asset file name & extension
     */
    name() {
      return this.asset.name + (this.extension ? `.${this.extension}` : '');
    },

    hasReferences() {
      const assetIds = this.getDescendantNodes(this.id, 'assets', 'children');
      assetIds.push(this.id);
      return (
        Object.keys(this.getAssetComponentReferences(assetIds)).length +
          Object.keys(this.getAssetElementReferences(assetIds)).length >
        0
      );
    },
  },

  methods: {
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    ...mapActions('project/data/assets', [
      'createAsset',
      'updateAsset',
      'deleteAsset',
      'move',
    ]),
    ...mapActions('application/modals', [
      'setAssetModal',
      'setDeleteAssetReferencesModal',
    ]),
    ...mapActions('application/notifications', ['createError']),

    /**
     * Custom event from svg icon load
     */
    async onIconLoad(svg) {
      // const url = `data:image/svg+xml;charset=utf-8,${encodeURIAsset(svg)}`;
      // this.assetImage = await loadImage(url);
    },

    onBlurText($event, asset) {
      const name = $event.target.value;
      if (isValidWindowsFilename(name)) {
        this.updateAsset({
          id: this.id,
          props: { name: name || 'Untitled asset' },
          apply: true,
        });
      } else {
        this.createError({ text: 'Invalid name for asset file or folder' });
      }

      this.isEditing = false;
      this.$refs.nameField.scrollLeft = -1000;
    },

    /**
     * Set this item in rename mode.
     */
    setRenameMode() {
      // Create input
      this.isEditing = true;

      // Because of v-if on isEditing the DOM is not rendered yet
      this.$nextTick(() => {
        const { nameInput } = this.$refs;
        nameInput.value = this.asset.name;
        nameInput.focus();
        // Set the selection range (instead of select()) to cover Safari also
        nameInput.setSelectionRange(0, nameInput.value.length);
      });
    },

    /**
     * Returns the container id
     */
    getContainerId() {
      const container = this.getContainerNode(this.id, 'assets', 'children');
      return container.id;
    },

    /**
     * Returns the container index in the container
     */
    getContainerIndex() {
      return this.getIndex(this.id, 'assets', 'children');
    },

    /**
     * Event listener - contextmenu event.
     */
    onContextMenu(event) {
      if (this.canEdit) {
        this.isSelected = true;
        this.createContextMenu(event.clientX, event.clientY);
      }
    },

    /**
     * Create a context menu for this asset
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      this.createItem({
        name: 'Upload new asset...',
        icon: null,
        commands: {
          doAction: () => {
            emitter.emit(EVENT.OPEN_FILE_UPLOADER, this.id, false, false);
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
      });
      if (!this.getIsRecursiveContainer(this.id)) {
        this.createItem({
          name: 'Replace asset...',
          icon: null,
          commands: {
            doAction: () => {
              emitter.emit(EVENT.OPEN_FILE_UPLOADER, this.id, false, true);
              this.deleteMenu();
            },
            isActive: () => true,
            isEnabled: () => true,
          },
        });
      }
      this.createItem({
        name: 'Create Folder',
        icon: null,
        commands: {
          doAction: () => {
            // Get default container
            let tId = this.getContainerId();
            // Get default tIndex
            let tIndex = this.getContainerIndex();
            if (this.getIsRecursiveContainer(this.id)) {
              // Get Current item instead of container ( current item as container and tIndex 0 )
              tId = this.id;
              tIndex = 0;
            }
            this.createAsset({
              props: assetTypes.newContainer.data,
              tId,
              tIndex,
              tExpand: true,
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
      });
      this.createItem({
        name: 'Rename',
        icon: null,
        commands: {
          doAction: () => {
            this.setRenameMode();
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
      });
      this.createItem({
        name: 'Delete',
        icon: null,
        commands: {
          doAction: () => {
            if (this.hasReferences) {
              this.setDeleteAssetReferencesModal({
                value: this.asset.id,
              });
            } else {
              this.deleteAsset({
                id: this.id,
                apply: true,
              });
            }
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });

      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          this.isSelected = false;
        },
      });
    },

    onClick() {
      this.$emit('clickEvent');
    },
  },
};
</script>
<style scoped lang="scss">
.item.asset {
  position: relative;
  cursor: pointer;
  overflow: hidden;

  .thumb:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    box-shadow: 0px 0px 0px 2px inset transparent;
    transition: box-shadow 0.25s;
  }
  &:hover .thumb:after {
    content: '';
    box-shadow: 0px 0px 0px 2px inset #db841e;
  }

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 8px;
    text-align: center;
    font-size: 11px;
    line-height: 1.5;
    // word-break: break-all;
    white-space: nowrap;
    .nameinput {
      font-family: 'Open Sans', sans-serif;
      color: #db841e;
      overflow: hidden;
      width: 100%;
      background: #0d0e0e;
      border: none;
      cursor: text;
      text-align: left;
      font-size: 12px;
      -webkit-touch-callout: auto;
      -webkit-user-select: auto;
      -khtml-user-select: auto;
      -moz-user-select: auto;
      -ms-user-select: auto;
      user-select: auto;
    }
  }
  .thumb {
    width: 100%;
    max-width: 100%;
    height: 84px;
    // max-height: 100px;
    background: #1e2121;
    position: relative;
    overflow: hidden;

    .folder-icon {
      height: 100%;
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}
</style>
